import React, { useState } from "react";
import TableSearch from "./TableSearch";
import TablePagination from "./TablePagination";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { DndContext, closestCenter } from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

// Şimdiki tablo
const Table = ({
  data,
  columns,
  translations = {},
  getText = (key, defVal) => defVal,
  enableRowSort = false,
  onRowSortEnd = () => {},
  rowIdKey = "id",
}) => {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      pagination,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
  });

  const SortableRow = ({ row, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: row.original.id,
      });

    return (
      <tr
        ref={setNodeRef}
        {...attributes}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
      >
        {/* listeners’ı children’a CONTEXT olarak veriyoruz */}
        {React.Children.map(children, (child) =>
          React.cloneElement(child, {
            dragListeners: listeners,
          })
        )}
      </tr>
    );
  };

  return (
    <div className="col-lg-12">
      <div className="card stretch stretch-full function-table">
        <div className="card-body p-0">
          <div className="table-responsive">
            <div className="dataTables_wrapper dt-bootstrap5 no-footer">
              {/* TableSearch bileşenine de translations, getText aktar */}
              <TableSearch
                table={table}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                translations={translations}
                getText={getText}
              />

              {/* Tablonun kendisi */}
              <div className="row dt-row">
                <div className="col-sm-12 px-0">
                  <table
                    className="table table-hover dataTable no-footer"
                    id="projectList"
                  >
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            return (
                              <th
                                key={header.id}
                                className={
                                  header.column.columnDef.meta?.headerClassName
                                }
                              >
                                {header.id === "id" ? (
                                  <div className="d-flex gap-2">
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                    <ArrowToggle header={header} />
                                  </div>
                                ) : (
                                  <ArrowToggle header={header}>
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                  </ArrowToggle>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      ))}
                    </thead>
                    {enableRowSort ? (
                      <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={onRowSortEnd}
                      >
                        <SortableContext
                          items={data.map((d) => d[rowIdKey])}
                          strategy={verticalListSortingStrategy}
                        >
                          <tbody>
                            {table.getRowModel().rows.map((row) => (
                              <SortableRow key={row.id} row={row}>
                                {row.getVisibleCells().map((cell) => (
                                  <td
                                    key={cell.id}
                                    className={
                                      cell.column.columnDef.meta?.className
                                    }
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </td>
                                ))}
                              </SortableRow>
                            ))}
                          </tbody>
                        </SortableContext>
                      </DndContext>
                    ) : (
                      <tbody>
                        {table.getRowModel().rows.map((row) => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>

              {/* pagination */}
              <TablePagination
                table={table}
                translations={translations}
                getText={getText}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Table;

const ArrowToggle = ({ header, children }) => {
  const position = header.column.getIsSorted();
  return (
    <div
      className="table-head"
      style={{ cursor: header.column.getCanSort() ? "pointer" : "default" }}
      onClick={header.column.getToggleSortingHandler()}
    >
      {children}
      {
        {
          asc: <FaSortUp size={13} opacity={position === "asc" ? 1 : 0.125} />,
          desc: (
            <FaSortDown size={13} opacity={position === "desc" ? 1 : 0.125} />
          ),
        }[position]
      }
      {header.column.getCanSort() && !position ? (
        <FaSort size={13} opacity={0.125} />
      ) : null}
    </div>
  );
};
